import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { aabb2ByTwoPoints } from "geom/aabb2";
import { aabb2Height, aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { pickAtPixelRect } from "./gpuPicking";
import { LineStrokeMaterialFactory } from "./materials/lineStrokeMaterial";
import { prepareMaterialsForRender } from "./materials/prepareMaterialsForRender";
import { MSAAResolver } from "./MSAAResolver";
import { RenderScheduler } from "./RenderScheduler";
import type { SharedWebGL, SharedWebGLViewer } from "./SharedWebGL";
import type { SavedCameraPosition } from "./threejs/higharc/SavedCameraPosition";
import {
  savedCameraPositionFromCamera,
  savedCameraPositionMatches,
  savedCameraPositionNew,
} from "./threejs/higharc/SavedCameraPosition";
import type { THREEViewerForRendererBase } from "./THREEViewerBaseTypes";
import { getTotalCSSTransformScale } from "./utils";

type THREEViewerCanvas = HTMLCanvasElement & {
  higharcCleanup?: () => void;
};

export function setTHREEViewerCanvasCleanup(canvas: HTMLCanvasElement, cleanup: () => void): void {
  (canvas as THREEViewerCanvas).higharcCleanup = cleanup;
}

export function cleanupOrphanedTHREEViewerCanvases(container: HTMLElement): void {
  for (const canvas of container.querySelectorAll("canvas")) {
    (canvas as THREEViewerCanvas).higharcCleanup?.();
  }
}

export abstract class THREEViewerRendererBase implements SharedWebGLViewer {
  // Used only when MSAA is enabled.
  protected readonly msaaResolver: MSAAResolver;

  protected autoRotate: boolean = false;
  protected readonly raycaster = new THREE.Raycaster();

  protected clientWidth: number;
  protected clientHeight: number;

  private lastRenderWidth: number;
  private lastRenderHeight: number;

  private lastSceneGeneration: number = -1;

  private requestResizeListener: () => void;
  private disposed: boolean = false;

  // Per-frame cache for DOM reads. Invalidated by advancing frameCacheId via clearFrameCache().
  // All values are constant within a single rAF callback, so caching eliminates forced reflows
  // from redundant getBoundingClientRect() and getComputedStyle() calls.
  private frameCacheId = 0;
  private cachedContainerRect: DOMRect | undefined;
  private cachedContainerRectFrame = -1;
  private cachedCanvasRect: DOMRect | undefined;
  private cachedCanvasRectFrame = -1;
  private cachedCSSTransformScale: { totalScaleX: number; totalScaleY: number } | undefined;
  private cachedCSSTransformScaleFrame = -1;

  // Note that we can have two updateRequested values - this one marks this viewer as dirty, and the one in the scheduler marks the canvas as dirty.
  // In case the canvas is shared between multiple viewers, the viewer doesn't have its own scheduler.
  private updateRequested: boolean = false;
  private readonly scheduler: RenderScheduler | undefined;

  private readonly savedCameraPosition: SavedCameraPosition = savedCameraPositionNew();

  public container: HTMLElement | null | undefined;
  protected viewer: THREEViewerForRendererBase;
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly sharedWebGL: SharedWebGL | null;
  public pixelRatio: number; // Pixel ratio used to render the viewer.

  get renderOrder(): number {
    return this.viewer.renderOrder;
  }

  protected suspenders = new Set<string>(); // The renderer is suspended if there's anything in this set.

  constructor({
    container,
    viewer,
    useMSAA,
    renderer,
    canvas,
    sharedWebGL,
    pixelRatio,
  }: {
    container: HTMLElement | null | undefined;
    viewer: THREEViewerForRendererBase;
    useMSAA: boolean;
    renderer: THREE.WebGLRenderer;
    canvas: HTMLCanvasElement;
    sharedWebGL: SharedWebGL | null | undefined;
    pixelRatio: number; // Pixel ratio used to render the viewer.
  }) {
    this.container = container;
    this.viewer = viewer;
    this.renderer = renderer;
    this.canvas = canvas;
    this.sharedWebGL = sharedWebGL ?? null;
    this.pixelRatio = pixelRatio;

    this.requestResizeListener = () => this.requestResize();
    window.addEventListener("resize", this.requestResizeListener, false);

    const rect = container?.getBoundingClientRect();
    const hasContainerSize = rect && rect.width > 0 && rect.height > 0;
    this.clientWidth = hasContainerSize ? rect.width : 1;
    this.clientHeight = hasContainerSize ? rect.height : 1;
    if (container) {
      const { totalScaleX, totalScaleY } = getTotalCSSTransformScale(container);
      // The Math.max prevents division by zero.
      this.clientWidth /= Math.max(totalScaleX, 0.001);
      this.clientHeight /= Math.max(totalScaleY, 0.001);
    }

    const { renderPixelWidth, renderPixelHeight } = this.getRenderPixelSize();
    this.lastRenderWidth = renderPixelWidth;
    this.lastRenderHeight = renderPixelHeight;

    if (this.sharedWebGL) {
      this.sharedWebGL.registerViewer(this);
    } else {
      renderer.setSize(renderPixelWidth, renderPixelHeight, false);
      this.canvas.style.width = this.clientWidth + "px";
      this.canvas.style.height = this.clientHeight + "px";
      this.scheduler = new RenderScheduler(
        () => this.viewer.applyAnimation(),
        () => this.render(),
      );
      this.scheduler.animate = this.viewer.animate;
    }

    this.msaaResolver = new MSAAResolver(renderPixelWidth, renderPixelHeight, useMSAA);
  }

  getRenderPixelSize() {
    if (this.sharedWebGL) {
      const { minX, minY, maxX, maxY } = this.getCanvasPixelAABB().aabb;
      const canvasAABBWidth = maxX - minX;
      const canvasAABBHeight = maxY - minY;
      if (window.devicePixelRatio > this.pixelRatio + 0.01) {
        // The render pixel ratio can be clamped to some smaller value than the device pixel ratio.
        const multiplier = this.pixelRatio / window.devicePixelRatio;
        return {
          renderPixelWidth: Math.round(canvasAABBWidth * multiplier),
          renderPixelHeight: Math.round(canvasAABBHeight * multiplier),
        };
      }
      return { renderPixelWidth: canvasAABBWidth, renderPixelHeight: canvasAABBHeight };
    }
    const width = Math.round(this.clientWidth * this.pixelRatio);
    const height = Math.round(this.clientHeight * this.pixelRatio);
    return { renderPixelWidth: Math.max(width, 1), renderPixelHeight: Math.max(height, 1) };
  }

  protected getPixelRatio(): number {
    return this.pixelRatio;
  }

  protected setPixelRatio(pixelRatio: number) {
    this.pixelRatio = pixelRatio;
    const { renderPixelWidth, renderPixelHeight } = this.getRenderPixelSize();
    const { msaaTargetWidth, msaaTargetHeight } = this.setRendererSize(renderPixelWidth, renderPixelHeight);
    this.msaaResolver.setSize(msaaTargetWidth, msaaTargetHeight);
    this.markRendererForUpdate();
  }

  /**
   * useTHREEViewerSystem may re-host the viewer into a new parent element.
   */
  setContainer(container: HTMLElement | undefined) {
    this.container = container;
    if (!this.sharedWebGL) {
      if (this.container) {
        this.container.appendChild(this.renderer.domElement);
      } else {
        this.renderer.domElement.parentElement?.removeChild(this.renderer.domElement);
      }
    }
    this.resizeRequested = true;
  }

  private screenPt: THREE.Vector2 = new THREE.Vector2();

  protected updateWorldToPixelFactor(
    camera: THREE.Camera,
    renderTargetClientSize: [number, number],
    appliedPixelRatio: number,
  ) {
    if (camera instanceof THREE.OrthographicCamera) {
      this.screenPt.x = -1;
      this.screenPt.y = 1;
      this.raycaster.setFromCamera(this.screenPt, camera);
      const cornerOrigin = this.raycaster.ray.origin.clone();

      this.screenPt.x = (1 / renderTargetClientSize[0]) * 2 - 1;
      this.screenPt.y = 1;
      this.raycaster.setFromCamera(this.screenPt, camera);
      const dist = this.raycaster.ray.origin.distanceTo(cornerOrigin);

      LineStrokeMaterialFactory.instance.setWorldToPixelFactor((dist * 1.001) / appliedPixelRatio);
    } else {
      LineStrokeMaterialFactory.instance.setWorldToPixelFactor(1.0 / appliedPixelRatio);
    }
  }

  dispose(): boolean {
    // only dispose once
    if (this.disposed) {
      return false;
    }

    this.disposed = true;
    this.msaaResolver.dispose();
    window.removeEventListener("resize", this.requestResizeListener, false);
    if (this.sharedWebGL) {
      this.sharedWebGL.deregisterViewer(this);
    } else {
      // Note that we purposefully don't dispose the three.js WebGLRenderer here.
      // It can still be reused via activeSharedWebGL.
      // Some resources like the design material texture atlas can be cached on it.
      //
      // Detach the canvas from whatever parent it currently has, not the construction-time
      // container. The canvas may have been reparented (via `setContainer`), or its parent
      // may have been replaced by HMR / React StrictMode mount→remount, in which cases an
      // equality check against `this.container` would short-circuit and leak the canvas.
      this.renderer.domElement.parentNode?.removeChild(this.renderer.domElement);
      this.viewer?.removeContextLostListener();
      this.scheduler?.dispose();
    }
    return true;
  }

  pickEntityIds(clientRect: AABB2): number[] {
    if (!this.container) {
      return [];
    }

    const rect = this.container.getBoundingClientRect();

    const canvasPixelAabb = this.getCanvasPixelAABB();
    if (!canvasPixelAabb.valid) {
      return [];
    }
    const canvasPixelWidth = aabb2Width(canvasPixelAabb.aabb);
    const canvasPixelHeight = aabb2Height(canvasPixelAabb.aabb);

    const canvasX = (clientX) => Math.round(((clientX - rect.left) / (rect.right - rect.left)) * canvasPixelWidth);
    const canvasY = (clientY) => Math.round(((clientY - rect.top) / (rect.bottom - rect.top)) * canvasPixelHeight);

    const canvasRect = aabb2ByTwoPoints(
      canvasX(clientRect.minX),
      canvasY(clientRect.minY),
      canvasX(clientRect.maxX),
      canvasY(clientRect.maxY),
    );
    if (aabb2Width(canvasRect) === 0) {
      canvasRect.maxX += 1;
    }
    if (aabb2Height(canvasRect) === 0) {
      canvasRect.maxY += 1;
    }

    return pickAtPixelRect(
      this.renderer,
      this.viewer.scene.scene,
      this.viewer.scene.generation,
      this.viewer.camera,
      canvasRect,
      canvasPixelWidth,
      canvasPixelHeight,
      this.viewer.scene.highlightDataTexture,
    );
  }

  /**
   * Return the current canvas contents as an image. For generating high-quality 3D screenshots, you should use screenshotOfBuilding instead.
   */
  async renderScreenshot(type: "image/png" | "image/jpeg" = "image/png", quality: number = 1): Promise<Blob | null> {
    return new Promise<Blob | null>((resolve) => {
      this.render();
      this.canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        type,
        quality,
      );
    });
  }

  clearFrameCache() {
    this.frameCacheId++;
  }

  private getContainerRect(): DOMRect {
    if (this.cachedContainerRectFrame !== this.frameCacheId) {
      assertIsNotNullOrUndefined(this.container);
      this.cachedContainerRectFrame = this.frameCacheId;
      this.cachedContainerRect = this.container.getBoundingClientRect();
    }
    assertIsNotUndefined(this.cachedContainerRect);
    return this.cachedContainerRect;
  }

  private getCanvasRect(): DOMRect {
    if (this.cachedCanvasRectFrame !== this.frameCacheId) {
      this.cachedCanvasRectFrame = this.frameCacheId;
      this.cachedCanvasRect = this.canvas.getBoundingClientRect();
    }
    assertIsNotUndefined(this.cachedCanvasRect);
    return this.cachedCanvasRect;
  }

  private getCSSTransformScale(): { totalScaleX: number; totalScaleY: number } {
    if (this.cachedCSSTransformScaleFrame !== this.frameCacheId) {
      assertIsNotNullOrUndefined(this.container);
      this.cachedCSSTransformScaleFrame = this.frameCacheId;
      this.cachedCSSTransformScale = getTotalCSSTransformScale(this.container);
    }
    assertIsNotUndefined(this.cachedCSSTransformScale);
    return this.cachedCSSTransformScale;
  }

  private resizeRequested = false;
  requestResize() {
    this.resizeRequested = true;
    this.markRendererForUpdate();
  }

  onResize() {
    let clientWidth = 1;
    let clientHeight = 1;

    if (this.container) {
      // getBoundingClientRect() returns precise CSS pixels including the fractional part.
      const rect = this.getContainerRect();
      clientWidth = rect.width;
      clientHeight = rect.height;

      // We want the client size before the CSS transform is applied, problem is that getBoundingClientRect() returns the size after the transform.
      // Invert the scaling from the CSS transform. This assumes that the CSS transform doesn't apply shear or rotation.
      const { totalScaleX, totalScaleY } = this.getCSSTransformScale();
      // The Math.max prevents division by zero.
      clientWidth /= Math.max(totalScaleX, 0.001);
      clientHeight /= Math.max(totalScaleY, 0.001);
    }

    if (clientWidth > 0 && clientHeight > 0) {
      this.clientWidth = clientWidth;
      this.clientHeight = clientHeight;

      const { renderPixelWidth, renderPixelHeight } = this.getRenderPixelSize();
      this.setRendererSize(renderPixelWidth, renderPixelHeight);
      this.markRendererForUpdate();

      if (!this.sharedWebGL) {
        this.canvas.style.width = clientWidth + "px";
        this.canvas.style.height = clientHeight + "px";
      }
    }
  }

  /**
   * This should be called when the rendered image should be updated.
   * It's automatically called when the camera or scene is changed using the utility functions in this class.
   * In case the scene is mutated in some other way, it's better to call SSFXRendererScene.markRenderingNeedsUpdate() if possible.
   */
  markRendererForUpdate() {
    if (this.disposed) {
      return;
    }
    this.updateRequested = true;
    if (this.sharedWebGL) {
      // The shared WebGL context always animates so it can poll CSS transform changes.
      return;
    }
    assertIsNotUndefined(this.scheduler);
    this.scheduler.markRendererForUpdate();
  }

  setAnimate(value: boolean) {
    if (this.sharedWebGL) {
      return;
    }
    assertIsNotUndefined(this.scheduler);
    this.scheduler.animate = value;
  }

  getAnimate(): boolean {
    return this.viewer.animate;
  }

  applyAnimation(): void {
    this.viewer.applyAnimation();
  }

  render() {
    if (this.disposed) {
      return;
    }
    if (!this.container) {
      return;
    }
    this.clearFrameCache();
    this.updateBackbuffer();
    this.refreshCanvas();
  }

  updateBackbuffer(): boolean {
    const { renderPixelWidth, renderPixelHeight } = this.getRenderPixelSize();

    // We might be running a CSS animation, which doesn't trigger "resizeRequested",
    // but might still be changing the backbuffer size in case we're running on a shared WebGL canvas.
    const didResize =
      this.resizeRequested || this.lastRenderWidth !== renderPixelWidth || this.lastRenderHeight !== renderPixelHeight;

    if (this.viewer.scene.generation !== this.lastSceneGeneration) {
      this.updateRequested = true;
      this.lastSceneGeneration = this.viewer.scene.generation;
    }

    // Idle when nothing changed and SSAA converged. Auto-rotate sets updateRequested via the controls
    // "change" event, so animation still renders. Shared WebGL must tick every frame to poll CSS transforms.
    if (!didResize && !(this.updateRequested || this.needsMoreTemporalSamples()) && !this.sharedWebGL) {
      return false;
    }

    let resizeMarksCanvasNeedsRefresh = false;
    if (didResize) {
      this.resizeRequested = false;
      this.lastRenderWidth = renderPixelWidth;
      this.lastRenderHeight = renderPixelHeight;
      this.viewer.onResize();
      resizeMarksCanvasNeedsRefresh = true;
    }

    if (this.suspenders.size > 0) {
      return resizeMarksCanvasNeedsRefresh;
    }

    // We set the highlightDataTexture on materials right before render so that we can share the same materials between different viewers.
    prepareMaterialsForRender(this.viewer.highlightDataTexture);
    this.viewer.prepareRender();

    if (this.container) {
      const rect = this.getContainerRect();
      this.updateWorldToPixelFactor(this.viewer.camera, [rect.width, rect.height], this.pixelRatio);
    }
    this.updateBackbufferInternal(this.updateRequested);
    this.updateRequested = false;

    if (!savedCameraPositionMatches(this.viewer.camera, this.savedCameraPosition) || didResize) {
      savedCameraPositionFromCamera(this.viewer.camera, this.savedCameraPosition);
      this.viewer.onCameraChange();
    }
    return true;
  }

  getCanvasPixelAABB(): { valid: boolean; aabb: AABB2 } {
    if (!this.container || !this.sharedWebGL) {
      const width = Math.round(this.clientWidth * this.pixelRatio);
      const height = Math.round(this.clientHeight * this.pixelRatio);
      return { valid: true, aabb: aabb2New(0, 0, Math.max(width, 1), Math.max(height, 1)) };
    }
    const rect = this.getContainerRect();
    const canvasRect = this.getCanvasRect();
    if (canvasRect.width === 0 || canvasRect.height === 0 || rect.width === 0 || rect.height === 0) {
      // Avoid returning NaN values in a corner case.
      return { valid: true, aabb: aabb2New(0, 0, 1, 1) };
    }
    // The actual canvas size might be set later if we've just resized and
    // we're querying the pixel AABB in the beginning of the frame to update camera aspect ratio.
    // sharedWebGL stores the size that will be applied at the end of the frame.
    const { width: canvasWidth, height: canvasHeight } = this.sharedWebGL.getSize();
    let x = Math.round(((rect.x - canvasRect.x) / canvasRect.width) * canvasWidth);
    let y = Math.round(((canvasRect.bottom - rect.bottom) / canvasRect.height) * canvasHeight);
    let width = Math.round((rect.width / canvasRect.width) * canvasWidth);
    let height = Math.round((rect.height / canvasRect.height) * canvasHeight);
    x = Math.max(x, 0);
    y = Math.max(y, 0);
    width = Math.min(canvasWidth - x, width);
    height = Math.min(canvasHeight - y, height);
    return { valid: width > 0 && height > 0, aabb: aabb2New(x, y, x + width, y + height) };
  }

  protected clearQuad(aabb: AABB2, color: number, alpha: number) {
    // It's simplest to do limit the cleared area with the scissor rect.
    // We could also consider using a positioned quad to reduce the API surface we touch, but it might prevent some driver optimizations for clears.
    this.renderer.setScissor(aabb.minX, aabb.minY, aabb.maxX - aabb.minX, aabb.maxY - aabb.minY);
    this.renderer.setClearColor(color, alpha);
    this.renderer.clear(true, false, false);
  }

  /**
   * We can suspend rendering due to multiple reasons. Calls to suspend() and resume() must be balanced.
   */
  suspend(suspender: string) {
    this.suspenders.add(suspender);
  }

  resume(suspender: string) {
    this.suspenders.delete(suspender);
  }

  protected needsMoreTemporalSamples(): boolean {
    return false;
  }

  // Settled = nothing pending to draw and TAA has finished accumulating (2D has no TAA, so just not-dirty).
  isRenderSettled(): boolean {
    return !this.updateRequested && !this.needsMoreTemporalSamples();
  }

  protected abstract setRendererSize(renderPixelWidth: number, renderPixelHeight: number);

  protected abstract updateBackbufferInternal(updateRequested: boolean);
  abstract refreshCanvas();
}
